import { PinLocation } from '@griply/assets';
import { LocationSuggestion } from '@griply/types';
import { getLocationMapsUrl } from '@griply/utils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormLocationInput } from 'shared/components/ui/form-location-input';

import * as Styled from '../event-form.style';
import { EventRow } from '../event-row';

// opening maps must not also drop the click-to-edit surface underneath into edit mode
const stopPropagation = (event: React.MouseEvent<HTMLAnchorElement>) =>
  event.stopPropagation();

export type EventLocationRowProps = {
  value: string;
  // absent on the read-only invitee view, which renders no input to search from
  suggestions?: LocationSuggestion[];
  isSearching?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onChange?: (location: string) => void;
  onSelectSuggestion?: (suggestion: LocationSuggestion) => void;
};

export const EventLocationRow: React.FC<EventLocationRowProps> = ({
  value,
  suggestions,
  isSearching,
  disabled,
  isLoading,
  onChange,
  onSelectSuggestion,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const isReadOnly = !!disabled || !onChange;
  const mapsUrl = getLocationMapsUrl(value);

  const startEditing = () => setIsEditing(true);
  const stopEditing = () => setIsEditing(false);

  return (
    <EventRow icon={PinLocation} isLoading={isLoading} alignTop>
      {/* a blank address has no link to read, so the field stays put and keeps its placeholder */}
      {isReadOnly || (!isEditing && !!mapsUrl) ? (
        <Styled.LocationReadView
          $canEdit={!isReadOnly}
          onClick={isReadOnly ? undefined : startEditing}
        >
          <Styled.LocationText>
            {!!mapsUrl && (
              <Styled.LocationLink
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                onClick={stopPropagation}
              >
                {value}
              </Styled.LocationLink>
            )}
          </Styled.LocationText>
        </Styled.LocationReadView>
      ) : (
        <FormLocationInput
          value={value}
          onChange={onChange}
          placeholder={t('forms.event.fields.location.placeholder')}
          suggestions={suggestions ?? []}
          isSearching={isSearching}
          onSelectSuggestion={onSelectSuggestion}
          focusOnMount={isEditing}
          onFocus={startEditing}
          onBlur={stopEditing}
        />
      )}
    </EventRow>
  );
};
